<template>
	<page :title="'Project Dashboard - ' + project.id" :breadcrumbs="breadcrumbs">
		<template #actions>
			<v-menu
				v-model="statusMenu"
				offset-y
				:nudge-width="200"
				:close-on-content-click="false">
				<template #activator="{ on, attrs }">
					<fieldset>
						<legend>Project Status</legend>
						<v-btn
							:color="project.status.color"
							:dark="isDarkColor(project.status.color)"
							large
							min-width="200"
							elevation="0"
							v-bind="attrs"
							v-on="on">
							{{ project.status.name }}
						</v-btn>
					</fieldset>
				</template>
				<v-card v-if="length(statuses)">
					<v-card-text>
						<v-autocomplete
							v-model="statusId"
							:items="statuses"
							placeholder="Change Status"
							item-text="name"
							item-value="id"
							filled
							outlined
							clearable
							hide-details>
							<template #selection="data">
								<v-icon left :color="data.item.color">mdi-circle</v-icon>
								<span>{{ data.item.name }}</span>
							</template>
							<template #item="data">
								<v-icon left :color="data.item.color">mdi-circle</v-icon>
								<span>{{ data.item.name }}</span>
							</template>
						</v-autocomplete>
						<v-btn color="primary"
							class="mt-3"
							:disabled="! statusId || statusId === project.status.id"
							@click="updateStatus">
							Change Status
						</v-btn>
					</v-card-text>
				</v-card>
			</v-menu>
		</template>
		<v-row>
			<v-col cols="12" md="3">
				<v-sheet rounded>
					<v-card outlined color="#F9F9FB">
						<v-list
							nav
							dense
							class="sidebar-nav">
							<v-list-item-group
								v-model="selectedTab"
								mandatory>
								<v-list-item
									v-for="(item, i) in tabs"
									:key="i"
									active-class="list-item-selected"
									@click="userClickedList(i)">
									<v-list-item-icon>
										<v-icon v-text="item.icon"></v-icon>
									</v-list-item-icon>

									<v-list-item-content>
										<v-list-item-title>
											<div class="d-flex">
												<div class="align-self-center">{{ item.text }}</div>
												<v-chip v-if="item.count" small class="ml-auto">{{ item.count }}</v-chip>
											</div>
										</v-list-item-title>
									</v-list-item-content>
								</v-list-item>
							</v-list-item-group>
						</v-list>
					</v-card>
				</v-sheet>

				<v-sheet v-if="orderer"
					rounded
					class="mt-5">
					<v-card outlined color="#F9F9FB">
						<v-card-title class="align-center">
							<Link url="#" class="no-underline">{{ orderer.name }}</Link>
						</v-card-title>
						<v-card-subtitle class="text-uppercase pt-1 pb-2 grey--text">Orderer</v-card-subtitle>
						<v-divider class="thick" />
						<v-list color="#ffffff" dense>
							<v-list-item v-if="orderer.type">
								<v-list-item-icon>
									<v-icon>
										mdi-account
									</v-icon>
								</v-list-item-icon>

								<v-list-item-content>
									<v-list-item-title>{{ orderer.type }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>

							<v-list-item v-if="orderer.phone">
								<v-list-item-icon>
									<v-icon>
										mdi-phone
									</v-icon>
								</v-list-item-icon>

								<v-list-item-content>
									<v-list-item-title>{{ orderer.phone }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>

							<v-list-item v-if="orderer.email">
								<v-list-item-icon>
									<v-icon>
										mdi-email
									</v-icon>
								</v-list-item-icon>

								<v-list-item-content>
									<v-list-item-title>{{ orderer.email }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-list>
					</v-card>
				</v-sheet>

				<v-sheet v-if="client"
					rounded
					class="mt-5">
					<v-card outlined color="#F9F9FB">
						<v-card-title class="align-center text-break">
							<Link :href="route('clients.show', client)" class="no-underline">{{ client.name }}</Link>
						</v-card-title>
						<v-card-subtitle class="text-uppercase pt-1 pb-2 grey--text">Client</v-card-subtitle>
						<v-divider class="thick" />
						<v-list color="#ffffff" dense>
							<v-list-item v-if="client.billing_contact_name">
								<v-list-item-icon>
									<v-icon>
										mdi-account
									</v-icon>
								</v-list-item-icon>

								<v-list-item-content>
									<v-list-item-title>{{ client.billing_contact_name }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>

							<v-list-item v-if="client.billing_contact_phone">
								<v-list-item-icon>
									<v-icon>
										mdi-phone
									</v-icon>
								</v-list-item-icon>

								<v-list-item-content>
									<v-list-item-title>{{ client.billing_contact_phone }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>

							<v-list-item v-if="client.billing_contact_email">
								<v-list-item-icon>
									<v-icon>
										mdi-email
									</v-icon>
								</v-list-item-icon>

								<v-list-item-content>
									<v-list-item-title>{{ client.billing_contact_email }}</v-list-item-title>
								</v-list-item-content>
							</v-list-item>

							<v-list-item v-if="client.address1" class="mt-2">
								<v-list-item-icon>
									<v-icon>
										mdi-map-marker
									</v-icon>
								</v-list-item-icon>

								<v-list-item-content>
									<v-list-item-title>
										{{ client.address1 }}
										<span v-if="client.address2"><br>{{ client.address2 }}</span><br>
										{{ client.city }}, {{ client.state.name }} {{ client.zipcode }}
									</v-list-item-title>
								</v-list-item-content>
							</v-list-item>
						</v-list>
					</v-card>
				</v-sheet>
			</v-col>

			<v-col cols="12" md="9" class="project-body">
				<v-tabs v-model="selectedTab" vertical>
					<v-tabs-items v-model="selectedTab">
						<v-tab-item eager>
							<project-summary :project="project" />
							<properties :properties="properties" :project="project" />
							<reports :reports="reports" />
						</v-tab-item>

						<v-tab-item>
							<edit-project :project="project" />
						</v-tab-item>

						<v-tab-item v-if="auth.user_type === 1">
							<invoices :invoices="invoices" />
						</v-tab-item>

						<v-tab-item v-if="auth.user_type === 1">
							<payments :payments="payments" />
						</v-tab-item>

						<v-tab-item>
							<files :files="files" />
						</v-tab-item>
					</v-tabs-items>
				</v-tabs>
			</v-col>
		</v-row>
		<v-btn
			elevation="5"
			color="warning"
			dark
			fixed
			bottom
			right
			class="floating-top-btn justify-start"
			@click="toTop">
			<v-icon left class="pr-3 pl-1">mdi-arrow-up-bold</v-icon>
			Back to top
		</v-btn>
	</page>
</template>
<script>
import api from '@/api'
import Backend from '@/Layouts/Backend'
import Page from '@/Pages/Backend/Components/Page'
import ProjectSummary from './Components/ProjectSummary'
import EditProject from './Components/EditProject'
import Properties from './Components/Properties'
import Reports from './Components/Reports'
import Files from './Components/Files'
import Invoices from '@/Pages/Backend/Projects/Components/Invoices'
import Payments from '@/Pages/Backend/Projects/Components/Payments'

export default {
	components: {Page, ProjectSummary, EditProject, Properties, Reports, Files, Invoices, Payments },
	layout: Backend,
	props: {
		project: {
			type: [Array, Object],
			default: () => {},
			required: true
		},
		client: {
			type: [Array, Object],
			default: () => {}
		},
		orderer: {
			type: [Array, Object],
			default: () => {}
		},
		properties: {
			type: [Array, Object],
			default: () => {},
			required: true
		},
		reports: {
			type: [Array, Object],
			default: () => { return [] },
			required: false
		},
		files: {
			type: [Array, Object],
			default: () => { return [] },
			required: false
		},
		invoices: {
			type: [Array, Object],
			default: () => { return [] },
			required: false
		},
		payments: {
			type: [Array, Object],
			default: () => { return [] },
			required: false
		},
		counts: {
			type: Object,
			default: () => {
				return {
					properties: 0,
					invoices: 0,
					payments: 0,
					files: 0
				}
			},
			required: true
		}
	},
	data () {
		return {
			auth: this.$page.props.auth,
			statusMenu: false,
			statusId: null,
			statuses: [],
			selectedTab: 0,
			loadedTabs: [],
			tabs: [],
			breadcrumbs: [
				{ route: 'dashboard', label: 'Dashboard' },
				{ route: 'clients.show', params: { client: this.project.client }, label: this.project.client.name },
				{ route: 'projects.index', label: 'Projects' },
				{ label: this.project.name, id: this.project.id }
			]
		}
	},
	watch: {
		selectedTab (tabId) {
			this.$page.props.filters = {}
			this.loadTabId(tabId)
		},
		'counts.invoices' (newCount) {
			this.tabs[2].count = newCount
		},
		'counts.payments' (newCount) {
			this.tabs[3].count = newCount
		},
		'counts.files' (newCount) {
			this.tabs[4].count = newCount
		}
	},
	beforeMount () {
		let tabs = [
			{ icon: 'mdi-folder-check', text: 'Project Summary', value: 'summary' }
		]

		// Admin users get access to invoices and payments
		if (this.auth.user_type === 1 || this.auth.user_type === 2) {
			tabs.push(
				{ icon: 'mdi-pen', text: 'Edit Project', value: 'edit' }
			)
		}

		if (this.auth.user_type === 1) {
			tabs.push(
				{ icon: 'mdi-bank', text: 'Invoices', count: this.counts.invoices, value: 'invoices' },
				{ icon: 'mdi-bank-check', text: 'Payments', count: this.counts.payments, value: 'payments' }
			)
		}

		tabs.push(
			{ icon: 'mdi-folder-open', text: 'Project Files', count: this.counts.files, value: 'files' }
		)

		this.tabs = tabs
	},
	mounted () {
		this.getProjectStatuses()
		this.statusId = this.project.status.id
	},
	methods: {
		getProjectStatuses () {
			const method = 'GET'
			const data = {}

			const url = this.route('project-statuses.api.index')

			return api({
				method,
				url,
				data
			}).then(response => {
				this.statuses = response.data
			})
		},

		loadTabId (tabId) {
			// Reset query string, so we don't confuse the other data tables
			window.history.pushState({}, document.title, window.location.pathname)

			if (this.loadedTabs.includes(tabId)) {
				return
			}

			const reload = {
				2: ['invoices'],
				3: ['payments'],
				4: ['files']
			}

			if (reload[tabId]) {
				this.$inertia.reload({
					only: [...reload[tabId], ...['counts']],
					onFinish: () => this.loadedTabs.push(tabId)
				})
			}
		},

		userClickedList (index) {
			// User clicked the same tab already open
			// reload the data
			if (index === this.selectedTab) {
				this.loadedTabs.splice(this.loadedTabs.indexOf(index), 1)
				this.$nextTick(() => this.loadTabId(index))
			}
		},

		updateStatus () {
			const data = { status_id: this.statusId }
			this.$inertia.put(this.route('projects.update.status', this.project), data, {
				only: ['project', 'flash'],
				onFinish: () => this.statusMenu = false,
				preserveState: true,
				preserveScroll: true
			})
		},
		toTop () {
			window.scrollTo({ top: 0, behavior: 'smooth' })
		}
	}
}
</script>
